import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, Check, Pause, Play, Plus } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../components/Card";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { api, ApiError } from "../../lib/api";
import type { AdminTenantSummary } from "../../lib/me";

interface AdminTenantFull extends AdminTenantSummary {
  settings?: Record<string, unknown>;
}

interface AdminTenantResponse {
  tenant: AdminTenantFull;
  setupProgress?: { currentStep: string; firstInboundReceivedAt: string | null };
}

export default function AdminTenantDetail() {
  const { tenantId } = useParams();
  const [data, setData] = useState<AdminTenantResponse | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!tenantId) return;
    try {
      const r = await api.get<AdminTenantResponse>(`/api/admin/tenants/${tenantId}`);
      setData(r);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (data === undefined) return <div className="text-sm text-gray-500">Loading…</div>;
  if (data === null) return <div className="text-sm text-gray-500">Not found.</div>;

  const t = data.tenant;
  const isSuspended = t.status === "suspended";

  const action = async (path: string, body: unknown = {}) => {
    setError(null);
    try {
      await api.post(`/api/admin/tenants/${t.tenantId}/${path}`, body);
      await load();
    } catch (err) {
      setError((err as ApiError).message ?? "Action failed.");
    }
  };

  const patch = async (body: Record<string, unknown>) => {
    setError(null);
    try {
      await api.patch(`/api/admin/tenants/${t.tenantId}`, body);
      await load();
    } catch (err) {
      setError((err as ApiError).message ?? "Update failed.");
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/tenants" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-3.5 w-3.5" /> All tenants
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t.displayName}</h1>
          <p className="mt-1 text-sm text-gray-500">
            <code className="rounded bg-gray-100 px-1 text-xs">{t.tenantId}</code>
            <span className="mx-1 text-gray-300">·</span>
            <code className="rounded bg-gray-100 px-1 text-xs">support@{t.inboundSubdomain}</code>
          </p>
        </div>
        <div className="flex gap-2">
          {isSuspended ? (
            <Button size="sm" onClick={() => action("reactivate")}>
              <Play className="h-3.5 w-3.5" /> Reactivate
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={() => action("suspend", { reason: prompt("Reason for suspension?") || "" })}>
              <Pause className="h-3.5 w-3.5" /> Suspend
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PlanCard tenant={t} onPatch={patch} />
        <QuotaCard tenant={t} onPatch={patch} />
        <TrialCard tenant={t} onPatch={patch} onAction={action} />
        <ModelsCard tenant={t} onPatch={patch} />
        <RawDetails tenant={t} progress={data.setupProgress} />
      </div>
    </div>
  );
}

interface ModelOption {
  id: string;
  label: string;
  recommended?: boolean;
}

interface ModelChoices {
  classify: ModelOption[];
  reply: ModelOption[];
}

function ModelsCard({
  tenant,
  onPatch,
}: {
  tenant: AdminTenantFull;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
}) {
  const settings = (tenant.settings ?? {}) as { classifierModel?: string; replyModel?: string };
  const [classifierModel, setClassifierModel] = useState(settings.classifierModel ?? "");
  const [replyModel, setReplyModel] = useState(settings.replyModel ?? "");
  const [choices, setChoices] = useState<ModelChoices | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setClassifierModel(settings.classifierModel ?? "");
    setReplyModel(settings.replyModel ?? "");
  }, [settings.classifierModel, settings.replyModel]);

  useEffect(() => {
    api.get<ModelChoices>("/api/admin/models").then(setChoices).catch(() => setChoices(null));
  }, []);

  const dirty =
    classifierModel !== (settings.classifierModel ?? "") ||
    replyModel !== (settings.replyModel ?? "");

  const save = async () => {
    setSaving(true);
    try {
      await onPatch({
        models: {
          classifierModel: classifierModel.trim(),
          replyModel: replyModel.trim(),
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="AI models"
        description="Bedrock models used for classification and reply generation. Super-admin only — tenants cannot change these from their Settings."
      />
      <CardBody className="space-y-4">
        <ModelField
          label="Classifier model"
          hint="Fast model for intent matching. Haiku is the default."
          value={classifierModel}
          onChange={setClassifierModel}
          options={choices?.classify ?? []}
        />
        <ModelField
          label="Reply model"
          hint="Higher-quality model for drafting replies. Sonnet is the default."
          value={replyModel}
          onChange={setReplyModel}
          options={choices?.reply ?? []}
        />
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

function ModelField({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  options: ModelOption[];
  onChange: (v: string) => void;
}) {
  const inList = options.some((o) => o.id === value);
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <select
        value={inList ? value : "__custom"}
        onChange={(e) => {
          if (e.target.value === "__custom") return;
          onChange(e.target.value);
        }}
        className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label} {o.recommended ? "★" : ""}
          </option>
        ))}
        <option value="__custom">Custom model id…</option>
      </select>
      <Input
        placeholder="global.anthropic.claude-..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        hint={hint}
      />
    </div>
  );
}

function PlanCard({ tenant, onPatch }: { tenant: AdminTenantFull; onPatch: (body: Record<string, unknown>) => Promise<void> }) {
  const [plan, setPlan] = useState(tenant.plan);
  const [status, setStatus] = useState(tenant.status);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setPlan(tenant.plan); setStatus(tenant.status); }, [tenant.plan, tenant.status]);

  const dirty = plan !== tenant.plan || status !== tenant.status;

  const save = async () => {
    setSaving(true);
    try {
      await onPatch({ plan, status });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Plan & status" description="Change the tenant's billing plan and lifecycle status." />
      <CardBody className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-900">Plan</label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value as AdminTenantFull["plan"])}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option value="trial">Trial</option>
            <option value="paid">Paid</option>
            <option value="internal">Internal (AppGambit dogfood)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option value="trial_active">trial_active</option>
            <option value="trial_expired">trial_expired</option>
            <option value="active">active (paid/internal)</option>
            <option value="suspended">suspended</option>
          </select>
        </div>
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

// Trial defaults the tenant was provisioned with (see TENANT_DEFAULTS in
// packages/core/src/ddb.ts). If a paid/internal tenant is still at one of
// these values it almost certainly means the admin upgraded the plan but
// forgot to bump the quota.
const TRIAL_DEFAULT_QUOTAS = new Set([50, 100]);

function QuotaCard({ tenant, onPatch }: { tenant: AdminTenantFull; onPatch: (body: Record<string, unknown>) => Promise<void> }) {
  const [limit, setLimit] = useState(tenant.quotaEmailsLimit);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setLimit(tenant.quotaEmailsLimit); }, [tenant.quotaEmailsLimit]);

  const dirty = limit !== tenant.quotaEmailsLimit;
  const remaining = Math.max(0, tenant.quotaEmailsLimit - tenant.quotaEmailsUsed);
  const exhausted = remaining === 0;
  const paidButTinyQuota = (tenant.plan === "paid" || tenant.plan === "internal")
    && TRIAL_DEFAULT_QUOTAS.has(tenant.quotaEmailsLimit);

  const save = async () => {
    setSaving(true);
    try {
      await onPatch({ quotaEmailsLimit: limit });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Quota" description="Email cap enforced at the classifier. Inbound is rejected after this." />
      <CardBody className="space-y-3">
        {(paidButTinyQuota || exhausted) && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {exhausted && <p><strong className="text-amber-900">Quota exhausted.</strong> Inbound emails are being rejected. Bump the limit below to resume processing.</p>}
            {paidButTinyQuota && !exhausted && (
              <p><strong className="text-amber-900">Plan is {tenant.plan} but quota is still at the trial default ({tenant.quotaEmailsLimit}).</strong> Bump the limit so this tenant doesn't hit the trial cap.</p>
            )}
          </div>
        )}
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <div className="text-xs uppercase tracking-wide text-gray-500">Current usage</div>
          <div className="mt-1 font-mono">
            <span className="text-gray-900">{tenant.quotaEmailsUsed.toLocaleString()}</span>
            <span className="text-gray-400"> / </span>
            <span className="text-gray-900">{tenant.quotaEmailsLimit.toLocaleString()}</span>
            <span className="text-gray-500"> ({remaining.toLocaleString()} remaining)</span>
          </div>
        </div>
        <Input
          label="New limit"
          type="number"
          min={0}
          value={limit}
          onChange={(e) => setLimit(Math.max(0, Number(e.target.value) || 0))}
          hint="Set to 0 to effectively block all new email."
        />
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

function TrialCard({
  tenant,
  onPatch,
  onAction,
}: {
  tenant: AdminTenantFull;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
  onAction: (path: string, body?: unknown) => Promise<void>;
}) {
  const [extendDays, setExtendDays] = useState(7);
  const [extending, setExtending] = useState(false);

  const expiresIn = tenant.trialExpiresAt
    ? Math.ceil((new Date(tenant.trialExpiresAt).getTime() - Date.now()) / 86400000)
    : null;

  const extend = async () => {
    setExtending(true);
    try {
      await onPatch({ extendTrialDays: extendDays });
    } finally {
      setExtending(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Trial" description="Extend the trial window for demos or active customers." />
      <CardBody className="space-y-3">
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <div className="text-xs uppercase tracking-wide text-gray-500">Current expiry</div>
          <div className="mt-1 font-mono text-gray-900">
            {tenant.trialExpiresAt ? new Date(tenant.trialExpiresAt).toLocaleString() : "Not set"}
          </div>
          {expiresIn != null && (
            <div className="mt-1 text-xs text-gray-500">
              {expiresIn > 0 ? `${expiresIn} days from now` : `expired ${Math.abs(expiresIn)} days ago`}
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <Input
            label="Extend by (days)"
            type="number"
            min={1}
            max={365}
            value={extendDays}
            onChange={(e) => setExtendDays(Math.max(1, Math.min(365, Number(e.target.value) || 7)))}
          />
          <Button onClick={extend} loading={extending}>
            <Plus className="h-3.5 w-3.5" /> Extend
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Extending sets the new expiry to <code className="rounded bg-gray-100 px-1 text-xs">max(now, current_expiry) + days</code>.
        </p>
      </CardBody>
    </Card>
  );
}

function RawDetails({ tenant, progress }: { tenant: AdminTenantFull; progress?: { currentStep: string; firstInboundReceivedAt: string | null } }) {
  return (
    <Card>
      <CardHeader title="Tenant details" />
      <CardBody>
        <dl className="space-y-2 text-sm">
          <Row label="Slug" value={tenant.tenantId} />
          <Row label="Display name" value={tenant.displayName} />
          <Row label="Inbound subdomain" value={tenant.inboundSubdomain} />
          <Row label="Created at" value={new Date(tenant.createdAt).toLocaleString()} />
          <Row label="Updated at" value={new Date(tenant.updatedAt).toLocaleString()} />
          {progress && <Row label="Setup step" value={progress.currentStep} />}
          {progress?.firstInboundReceivedAt && (
            <Row label="First inbound received" value={new Date(progress.firstInboundReceivedAt).toLocaleString()} />
          )}
        </dl>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 pb-1 last:border-0">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-mono text-gray-900">{value}</dd>
    </div>
  );
}

function SaveRow({ saving, saved, disabled, onClick }: { saving: boolean; saved: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <div className="flex items-center justify-end gap-3">
      {saved && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <Check className="h-3.5 w-3.5" /> Saved
        </span>
      )}
      <Button size="sm" onClick={onClick} loading={saving} disabled={disabled}>
        <Save className="h-3.5 w-3.5" /> Save
      </Button>
    </div>
  );
}
